import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Camera, Loader2 } from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import { Button } from '../../components/ui/button'
import { useAuth } from '../../contexts/AuthContext'
import { useUnits } from '../../contexts/UnitsContext'
import { useProfile } from '../../hooks/useProfile'
import Avatar from '../../components/Avatar'
import { uploadAvatar } from '../../lib/avatarUpload'

// Standalone profile-edit page (NOT the onboarding wizard).
// Single form covering avatar + identity fields. The avatar uses an
// inline file picker — no stacked modal — so iOS only shows its
// native source-picker once when the user actually wants to swap
// the photo. Each field is its own controlled input; "Save changes"
// at the bottom flushes the diff via upsertProfile(patch).
export function ProfilePage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { profile, upsertProfile, upsertAvatar } = useProfile()
  const { lengthUnit, displayValueLength, lengthToCm } = useUnits()
  const locale = i18n.language === 'ar' ? 'ar' : 'en'

  const fileInputRef = useRef(null)
  const [name, setName] = useState('')
  const [birthday, setBirthday] = useState('')
  const [sex, setSex] = useState('')
  const [height, setHeight] = useState('')
  const [saving, setSaving] = useState(false)
  const [uploadingAvatar, setUploadingAvatar] = useState(false)
  const [avatarError, setAvatarError] = useState(null)
  const [savedAt, setSavedAt] = useState(null)

  // Seed the form with the latest profile row. Re-runs when the
  // profile fetch resolves so the inputs land populated.
  useEffect(() => {
    if (!profile) return
    setName(profile.name || '')
    setBirthday(profile.birthday || '')
    setSex(profile.sex || '')
    setHeight(profile.height_cm != null
      ? String(displayValueLength(profile.height_cm).toFixed(1))
      : '')
  }, [profile, displayValueLength])

  const handlePickAvatar = () => {
    fileInputRef.current?.click()
  }

  const handleAvatarFile = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || !user) return
    setAvatarError(null)
    setUploadingAvatar(true)
    const { url, error } = await uploadAvatar(user.id, file)
    if (error || !url) {
      setAvatarError(error || t('profile.avatar.saveFailed'))
      setUploadingAvatar(false)
      return
    }
    await upsertAvatar(url)
    setUploadingAvatar(false)
  }

  const handleSave = async () => {
    if (!user || saving) return
    setSaving(true)
    const heightCm = height ? lengthToCm(Number(height)) : null
    const patch = {
      name: name.trim() || null,
      birthday: birthday || null,
      sex: sex || null,
      height_cm: heightCm,
    }
    const ok = await upsertProfile(patch)
    setSaving(false)
    if (ok) setSavedAt(Date.now())
  }

  // Auto-clear the "saved" pill after a couple of seconds so it
  // doesn't sit there forever once the user looks away.
  useEffect(() => {
    if (!savedAt) return undefined
    const id = setTimeout(() => setSavedAt(null), 2500)
    return () => clearTimeout(id)
  }, [savedAt])

  return (
    <div className="space-y-4 content-fade pb-8">
      <div className="flex items-center gap-2 mb-2">
        <button
          type="button"
          onClick={() => navigate(-1)}
          aria-label={t('settings.back')}
          className="p-2 -ms-2 rounded-md hover:bg-accent text-foreground"
        >
          <ArrowLeft className="w-5 h-5 rtl:-scale-x-100" />
        </button>
        <h1 className="flex-1 min-w-0 text-lg font-bold text-foreground truncate" dir="auto">
          {t('profile.editPage.title')}
        </h1>
        {savedAt && (
          <span className="text-xs text-emerald-500" dir="auto">
            {t('profile.editPage.saved')}
          </span>
        )}
      </div>

      {/* Avatar section. Big circular photo with a Change button
          below. Hidden file input handles the OS picker — iOS shows
          its native sheet, no app modal layered on top. */}
      <Card>
        <CardContent className="p-5 flex flex-col items-center gap-3">
          <div className="relative">
            <Avatar userId={user?.id} profile={profile} size={112} className="border-2 border-app-primary-500/30" />
            {uploadingAvatar && (
              <span className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full">
                <Loader2 className="w-6 h-6 text-white animate-spin" />
              </span>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleAvatarFile}
            className="hidden"
          />
          <button
            type="button"
            onClick={handlePickAvatar}
            disabled={uploadingAvatar}
            className="inline-flex items-center gap-1.5 text-sm text-app-primary-500 hover:text-app-primary-400 disabled:opacity-50"
            dir="auto"
          >
            <Camera className="w-4 h-4" />
            <span>{t('profile.editPage.changePhoto')}</span>
          </button>
          {avatarError && (
            <p className="text-xs text-red-500 text-center" dir="auto">{avatarError}</p>
          )}
        </CardContent>
      </Card>

      {/* Identity fields. Each stays controlled and pushes its value
          through the single Save button at the bottom — no per-field
          auto-save so the user can experiment without each blur
          firing a network round-trip. */}
      <Card>
        <CardContent className="p-4 space-y-4">
          <Field label={t('profile.editPage.name')}>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('profile.editPage.namePh')}
              dir="auto"
              className="w-full bg-transparent border border-border rounded-md px-3 py-2 text-sm text-foreground focus:outline-none focus:border-app-primary-500"
            />
          </Field>

          <Field label={t('profile.editPage.birthday')}>
            <input
              type="date"
              value={birthday}
              onChange={(e) => setBirthday(e.target.value)}
              dir="ltr"
              className="w-full bg-transparent border border-border rounded-md px-3 py-2 text-sm text-foreground focus:outline-none focus:border-app-primary-500"
            />
          </Field>

          <Field label={t('profile.editPage.sex')}>
            <div className="grid grid-cols-3 gap-2">
              {['male', 'female', 'other'].map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setSex(opt === sex ? '' : opt)}
                  className={`py-2 rounded-md border-2 text-sm transition-colors ${
                    sex === opt
                      ? 'border-app-primary-500 bg-app-primary-500/10 text-app-primary-500 font-semibold'
                      : 'border-border text-foreground hover:bg-accent'
                  }`}
                  dir="auto"
                >
                  {t(`profile.editPage.sex_${opt}`)}
                </button>
              ))}
            </div>
          </Field>

          <Field label={t('profile.editPage.height', { unit: lengthUnit })}>
            <input
              type="number"
              inputMode="decimal"
              value={height}
              onChange={(e) => setHeight(e.target.value)}
              placeholder={lengthUnit === 'cm' ? '170' : '67'}
              dir="ltr"
              className="w-full bg-transparent border border-border rounded-md px-3 py-2 text-sm text-foreground focus:outline-none focus:border-app-primary-500"
            />
          </Field>
        </CardContent>
      </Card>

      <Button onClick={handleSave} disabled={saving} className="w-full">
        {saving ? t('profile.editPage.saving') : t('profile.editPage.save')}
      </Button>
    </div>
  )
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground block" dir="auto">
        {label}
      </label>
      {children}
    </div>
  )
}
